import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";

interface DeleteButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onDelete: () => void;
  initialText?: string;
  confirmText?: string;
}

const DeleteButton = ({
  onDelete,
  initialText = "Delete",
  confirmText = "Are you sure?",
  className,
  ...props
}: DeleteButtonProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [progressPercent, setProgressPercent] = useState(100);

  useEffect(() => {
    if (isConfirming) {
      setProgressPercent(100);

      let startTime: number;
      const duration = 3000; // 3 seconds

      const animateProgress = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration * 100));

        setProgressPercent(remaining);

        if (elapsed < duration) {
          requestId = requestAnimationFrame(animateProgress);
        }
      };

      let requestId = requestAnimationFrame(animateProgress);
      const timer = setTimeout(() => setIsConfirming(false), duration);

      return () => {
        clearTimeout(timer);
        cancelAnimationFrame(requestId);
      };
    }
  }, [isConfirming]);

  const handleClick = () => {
    if (isConfirming) {
      onDelete();
      setIsConfirming(false);
    } else {
      setIsConfirming(true);
    }
  };

  return (
    <Button
      type="button"
      variant={isConfirming ? "destructive" : "ghost"}
      onClick={handleClick}
      className={cn(
        "transition-colors relative overflow-hidden",
        isConfirming ? "text-white hover:bg-red-700 bg-red-600" : "text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-500/20",
        className
      )}
      {...props}
    >
      {isConfirming && (
        <div
          className="absolute inset-0 z-0"
          style={{
            background: "linear-gradient(to right, #dc2626, #ef4444)",
            clipPath: `inset(0 ${100 - progressPercent}% 0 0)`,
            transition: 'clip-path 16ms linear'
          }}
        />
      )}
      <span className="relative z-10 flex items-center">
        <Trash2 className="w-4 h-4 mr-2" />
        {isConfirming ? confirmText : <span className="hidden md:inline">{initialText}</span>}
      </span>
    </Button>
  );
};

export default DeleteButton;
